import type { IRecording } from '@carboncopyco/types';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';

interface ITabQueue {
  tabId: number;
  queueFunction: () => void;
}

const initialTab = {
  id: 1,
  type: TabType.BASIC,
  innerTabs: [],
  closedInnerTabs: [],
} as IGlobalTab;

export const TabContext = createContext({
  globalTabs: [] as IGlobalTab[],
  globalTabsRef: { current: [] as IGlobalTab[] },
  activeGlobalTabId: 1,
  activeGlobalTab: initialTab,
  activeRecordingData: undefined as IRecording | undefined,
  setActiveRecordingData: (
    _newActiveRecordingData: IRecording | undefined
  ) => {},
  setActiveGlobalTabId: (_activeGlobalTabId: number) => {},
  redirectRoute: '',
  setRedirectRoute: (_redirectRoute: string) => {},
  setGlobalTabs: (
    _globalTabs: IGlobalTab[] | ((prevTabs: IGlobalTab[]) => IGlobalTab[])
  ) => {},
  isOpenCloseTabDialog: false,
  setIsOpenCloseTabDialog: (_isOpenCloseTabDialog: boolean) => {},
  tabsQueue: [] as ITabQueue[],
  registerTabQueue: (_tabId: number, _queueFunction: () => void) => {},
  triggerTabQueue: (_tabId: number) => {},
  getIsModalOpenByTabId: (_tabId: number): boolean => false,
  setIsModalOpenByTabId: (_tabId: number, _state: boolean) => {},
  isActiveTabModalOpen: false,
  activeTabType: TabType.BASIC,
  setActiveTabType: (_activeTabType: TabType) => {},
  getAllGroupRelatedTabs: (_groupId: number): IGlobalTab[] => [],
  getGroupMainTab: (_tab: IGlobalTab): IGlobalTab | undefined => {},
});

export const useTabs = () => {
  return useContext(TabContext);
};

const TabContextProvider = ({ children }: { children: JSX.Element }) => {
  const [tabs, setTabs] = useState<IGlobalTab[]>([initialTab]);
  const tabsRef = useRef<IGlobalTab[]>([initialTab]);
  const [activeGlobalTabId, setActiveGlobalTabId] = useState(1);
  const [redirectRoute, setRedirectRoute] = useState('');
  const [isOpenCloseTabDialog, setIsOpenCloseTabDialog] = useState(false);
  const [tabsQueue, setTabsQueue] = useState<
    { tabId: number; queueFunction: () => void }[]
  >([]);
  const [activeTabType, setActiveTabType] = useState<TabType>(TabType.BASIC);
  const [activeRecordingData, setActiveRecordingData] = useState<
    IRecording | undefined
  >(undefined);

  const registerTabQueue = (tabId: number, queueFunction: () => void) => {
    setTabsQueue((prev) => [...prev, { tabId, queueFunction }]);
  };

  const unregisterTabQueue = (tabId: number) => {
    const queue = tabsQueue.filter((tab) => tab.tabId !== tabId);
    setTabsQueue(queue);
  };

  const triggerTabQueue = (tabId: number) => {
    const activeTab = tabs.find((tab) => tab.id === tabId);
    if (!activeTab) return;
    const tabQueue = tabsQueue.find((tab) => tab.tabId === tabId);
    if (isActiveOrAllInnerTabsLoaded(activeTab) && tabQueue) {
      tabQueue.queueFunction();
      unregisterTabQueue(activeTab.id);
    }
  };

  const getIsModalOpenByTabId = (tabId: number) => {
    const tab = tabs.find(({ id }) => id === tabId);
    return tab?.isModalOpen || false;
  };

  const setIsModalOpenByTabId = (tabId: number, state: boolean) => {
    const newTabs = tabs.map((tab) => {
      if (tab.id === tabId) {
        return { ...tab, isModalOpen: state };
      }
      return tab;
    });
    setTabs(newTabs);
  };

  const isActiveTabModalOpen =
    getIsModalOpenByTabId(activeGlobalTabId) || false;

  const activeGlobalTab = tabs.find(
    (tab) => tab.id === activeGlobalTabId
  ) as IGlobalTab;

  const handleSetGlobalTabs = (
    newTabsOrUpdater: IGlobalTab[] | ((prevTabs: IGlobalTab[]) => IGlobalTab[])
  ) => {
    const updatedTabs =
      typeof newTabsOrUpdater === 'function'
        ? newTabsOrUpdater(tabsRef.current)
        : newTabsOrUpdater;

    tabsRef.current = updatedTabs;
    setTabs(updatedTabs);
  };

  useEffect(() => {
    tabsRef.current = tabs;
  }, []);

  const getAllGroupRelatedTabs = (groupId: number) => {
    return tabsRef.current.filter(
      (tab) => tab.group && tab?.group?.id === groupId
    );
  };

  const getGroupMainTab = (tab: IGlobalTab) => {
    if (tab?.group?.id) {
      const groupTabs = getAllGroupRelatedTabs(tab?.group?.id);
      return groupTabs.find((tab) => tab.group?.isVisibleIfGroupIsInactive);
    }

    return;
  };
  return (
    <TabContext.Provider
      value={{
        getAllGroupRelatedTabs,
        getGroupMainTab,
        globalTabs: tabs,
        globalTabsRef: tabsRef,
        activeGlobalTabId,
        activeGlobalTab,
        setActiveGlobalTabId,
        redirectRoute,
        setRedirectRoute,
        setGlobalTabs: handleSetGlobalTabs,
        isOpenCloseTabDialog,
        setIsOpenCloseTabDialog,
        tabsQueue,
        registerTabQueue,
        triggerTabQueue,
        getIsModalOpenByTabId,
        setIsModalOpenByTabId,
        isActiveTabModalOpen,
        activeTabType,
        setActiveTabType,
        activeRecordingData,
        setActiveRecordingData,
      }}
    >
      {children}
    </TabContext.Provider>
  );
};

export { TabContextProvider };
